import { useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { type FormData, PERSONALITY_QUESTIONS } from "./types";
import "./StepPersonality.scss";

// ─── Step 3: Work Personality ─────────────────────────────────────────────────

// mode: "answering" → activeIndex is the current question (0-4); answered ones shown as rows above
// mode: "summary"   → all 5 answered, all shown as rows, Continue button visible
// mode: "changing"  → changeIndex question shows options in-place; resumeIndex (-1 if none) is the
//                     unanswered in-progress question that was active before Change was clicked
type Step3Mode =
  | { mode: "answering"; activeIndex: number }
  | { mode: "summary" }
  | { mode: "changing"; changeIndex: number; resumeIndex: number };

function StepPersonality({
  data,
  onChange,
  onBack,
  onContinue,
  editMode = false,
}: {
  data: FormData;
  // eslint-disable-next-line no-unused-vars
  onChange: (_patch: Partial<FormData>) => void;
  onBack: () => void;
  onContinue: () => void;
  editMode?: boolean;
}) {
  const [state, setState] = useState<Step3Mode>(() => {
    const answered = PERSONALITY_QUESTIONS.filter((q) => !!(data[q.key] as string)).length;
    if (answered === PERSONALITY_QUESTIONS.length) return { mode: "summary" };
    return { mode: "answering", activeIndex: answered };
  });

  const handleSelectOption = (questionIndex: number, value: string) => {
    const q = PERSONALITY_QUESTIONS[questionIndex];
    onChange({ [q.key]: value });
    if (state.mode === "changing") {
      // Find the first question that is still unanswered (excluding the one just answered)
      const nextUnanswered = PERSONALITY_QUESTIONS.findIndex(
        (pq, idx) => idx !== questionIndex && !(data[pq.key] as string)
      );
      if (nextUnanswered === -1) {
        setState({ mode: "summary" });
      } else {
        setState({ mode: "answering", activeIndex: nextUnanswered });
      }
    } else if (questionIndex === PERSONALITY_QUESTIONS.length - 1) {
      setState({ mode: "summary" });
    } else {
      setState({ mode: "answering", activeIndex: questionIndex + 1 });
    }
  };

  const handleBack = () => {
    if (state.mode === "summary" || state.mode === "changing") {
      // In summary or change mode → go back to previous step in wizard
      onBack();
    } else if (state.activeIndex === 0) {
      onBack();
    } else {
      setState({ mode: "answering", activeIndex: state.activeIndex - 1 });
    }
  };

  // Renders a single answered question row
  const renderAnsweredRow = (i: number) => {
    const q = PERSONALITY_QUESTIONS[i];
    const selectedValue = data[q.key] as string;
    const selectedOption = q.options.find((o) => o.value === selectedValue);
    return (
      <div key={q.key as string} className="rah-personality__summary-row">
        <div className="rah-personality__summary-text">
          <span className="rah-personality__summary-question">{q.question}</span>
          <span className="rah-personality__summary-answer">{selectedOption?.label ?? "—"}</span>
        </div>
        <button
          type="button"
          className="rah-personality__change-btn"
          onClick={() => {
            let resumeIndex = -1;
            if (state.mode === "answering") resumeIndex = state.activeIndex;
            else if (state.mode === "changing") resumeIndex = state.resumeIndex;
            setState({ mode: "changing", changeIndex: i, resumeIndex });
          }}
        >
          Change
        </button>
      </div>
    );
  };

  // Renders the question + options block for a given index
  const renderQuestionBlock = (questionIndex: number) => {
    const q = PERSONALITY_QUESTIONS[questionIndex];
    const currentValue = data[q.key] as string;
    return (
      <div key={`q-${questionIndex}`} className="rah-personality__question-block">
        <div className="rah-personality__header-group">
          <span className="rah-personality__category">{q.category}</span>
          <p className="rah-personality__question">{q.question}</p>
        </div>
        <div className="rah-personality__options">
          {q.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`rah-personality__option${currentValue === opt.value ? " rah-personality__option--selected" : ""}`}
              onClick={() => handleSelectOption(questionIndex, opt.value)}
            >
              <span className="rah-personality__option-title">{opt.label}</span>
              <span className="rah-personality__option-desc">{opt.description}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Build the body content: a list of row/block elements in question order
  const renderBody = () => {
    if (state.mode === "answering") {
      const answeredBeforeActive = PERSONALITY_QUESTIONS.map((q, i) => ({ q, i })).filter(
        ({ q, i }) => i !== state.activeIndex && !!(data[q.key] as string)
      );
      return (
        <div className="rah-personality__body">
          {answeredBeforeActive.length > 0 && (
            <div className="rah-personality__answered-stack">
              {answeredBeforeActive.map(({ i }) => renderAnsweredRow(i))}
            </div>
          )}
          {renderQuestionBlock(state.activeIndex)}
        </div>
      );
    }

    if (state.mode === "changing") {
      return (
        <div className="rah-personality__body">
          {PERSONALITY_QUESTIONS.map((q, i) => {
            if (i === state.changeIndex) return renderQuestionBlock(i);
            // The in-progress question that was active before Change was clicked —
            // keep it visible as a question block so it doesn't disappear
            if (i === state.resumeIndex) return renderQuestionBlock(i);
            // Only show answered rows for questions that actually have a saved answer
            if (!(data[q.key] as string)) return null;
            return renderAnsweredRow(i);
          })}
        </div>
      );
    }

    // summary
    return <div className="rah-personality__body">{PERSONALITY_QUESTIONS.map((_, i) => renderAnsweredRow(i))}</div>;
  };

  const isSummary = state.mode === "summary";

  return (
    <div className="rah-form-card">
      <div className="rah-form-card__header">
        <h2 className="rah-form-card__title">What&apos;s their work personality?</h2>
        <p className="rah-form-card__subtitle">Answer 5 quick questions to help us find the right fit</p>
      </div>

      <div className="rah-form-card__fields" style={{ paddingBottom: 24 }}>
        {renderBody()}
      </div>

      <div className="rah-form-card__footer">
        <button type="button" className="rah-form-card__back-btn" onClick={handleBack}>
          <ChevronLeftIcon sx={{ fontSize: 22 }} />
          Back
        </button>
        {isSummary || editMode ? (
          <button type="button" className="rah-form-card__continue-btn" onClick={onContinue}>
            {editMode ? "Save" : "Continue"}
            <ChevronRightIcon sx={{ fontSize: 22 }} />
          </button>
        ) : (
          <button type="button" className="rah-personality__skip-btn" onClick={onContinue}>
            Skip this step
          </button>
        )}
      </div>
    </div>
  );
}

export default StepPersonality;
